import * as RadixMenu from '@radix-ui/react-dropdown-menu'
import { Check } from 'lucide-react'
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import styles from './DropdownMenu.module.scss'

export const DropdownMenuRoot = RadixMenu.Root
export const DropdownMenuTrigger = RadixMenu.Trigger

export const DropdownMenuContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixMenu.Content>
>(function DropdownMenuContent({ className, sideOffset = 6, children, ...rest }, ref) {
  return (
    <RadixMenu.Portal>
      <RadixMenu.Content ref={ref} sideOffset={sideOffset} className={cn(styles.content, className)} {...rest}>
        {children}
      </RadixMenu.Content>
    </RadixMenu.Portal>
  )
})

export const DropdownMenuLabel = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixMenu.Label>
>(function DropdownMenuLabel({ className, ...rest }, ref) {
  return <RadixMenu.Label ref={ref} className={cn(styles.label, className)} {...rest} />
})

export interface DropdownMenuItemProps extends ComponentPropsWithoutRef<typeof RadixMenu.Item> {
  leadingIcon?: ReactNode
  hint?: ReactNode
  danger?: boolean
}

export const DropdownMenuItem = forwardRef<HTMLDivElement, DropdownMenuItemProps>(
  function DropdownMenuItem({ leadingIcon, hint, danger, className, children, ...rest }, ref) {
    return (
      <RadixMenu.Item
        ref={ref}
        className={cn(styles.item, danger && styles.danger, className)}
        {...rest}
      >
        {leadingIcon ? <span className={styles.itemIcon} aria-hidden>{leadingIcon}</span> : null}
        <span className={styles.itemLabel}>{children}</span>
        {hint ? <span className={styles.itemHint}>{hint}</span> : null}
      </RadixMenu.Item>
    )
  },
)

export interface DropdownMenuCheckboxItemProps
  extends ComponentPropsWithoutRef<typeof RadixMenu.CheckboxItem> {
  children: ReactNode
}

export const DropdownMenuCheckboxItem = forwardRef<HTMLDivElement, DropdownMenuCheckboxItemProps>(
  function DropdownMenuCheckboxItem({ className, children, ...rest }, ref) {
    return (
      <RadixMenu.CheckboxItem ref={ref} className={cn(styles.item, styles.checkable, className)} {...rest}>
        <span className={styles.itemIcon} aria-hidden>
          <RadixMenu.ItemIndicator>
            <Check />
          </RadixMenu.ItemIndicator>
        </span>
        <span className={styles.itemLabel}>{children}</span>
      </RadixMenu.CheckboxItem>
    )
  },
)

export const DropdownMenuSeparator = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixMenu.Separator>
>(function DropdownMenuSeparator({ className, ...rest }, ref) {
  return <RadixMenu.Separator ref={ref} className={cn(styles.separator, className)} {...rest} />
})
